import { useState, useEffect } from 'react';
import Navbar from '../components/Navbar';
import { supabase } from '../lib/supabase';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { Users, Trash2, Search, Edit3, ChevronUp, ChevronDown, Filter, X, Plus } from 'lucide-react';
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from '../components/ui/table';
import { formatDateString } from '../lib/utils';

// shadcn/ui components
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';

interface PlayerRow {
  id: string;
  first_name?: string;
  last_name?: string;
  sex?: string; // Some components use gender and others use sex
  birthdate?: string;
  shirt_size?: string;
  age_group_id?: string;
  age_group?: AgeGroup;
  profile?: {
    positions?: string[];
    competitiveness?: string[];
    notes?: string;
  };
}

interface AgeGroup {
  id: string;
  group_name: string;
  sex: string;
  birth_date_min?: string;
  birth_date_max?: string;
}

type SortField = 'name' | 'birthdate';
type SortDirection = 'asc' | 'desc';

export default function PlayerManagement() {
  const { profile } = useAuth();
  const navigate = useNavigate();

  const [players, setPlayers] = useState<PlayerRow[]>([]);
  const [loadingPlayers, setLoadingPlayers] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [sortField, setSortField] = useState<SortField>('name');
  const [sortDirection, setSortDirection] = useState<SortDirection>('asc');

  // Filter states
  const [ageGroups, setAgeGroups] = useState<AgeGroup[]>([]);
  const [showFilters, setShowFilters] = useState(false);
  const [genderFilter, setGenderFilter] = useState<string>('all');
  const [ageGroupFilter, setAgeGroupFilter] = useState<string>('all');
  const [positionsFilter, setPositionsFilter] = useState<string[]>([]);
  const [competitivenessFilter, setCompetitivenessFilter] = useState<string[]>([]);
  const [loadingAgeGroups, setLoadingAgeGroups] = useState(false);

  // Constants for filter options
  const positionsOptions = ['Goalkeeper', 'Defender', 'Midfielder', 'Forward'];
  const competitivenessOptions = ['Premier', 'Elite', 'Alliance'];

  const handleRemovePlayer = async (playerId: string) => {
    const confirmRemove = window.confirm(
      'Are you sure you want to remove this player? This action is permanent.'
    );
    if (!confirmRemove) return;

    try {
      // Call the Supabase function to delete the player
      const { error: rpcError } = await supabase.rpc('delete_player', {
        player_id_to_delete: playerId,
      });

      if (rpcError) {
        // Handle specific permission error from the function
        if (rpcError.message.includes('Insufficient permissions')) {
           setError('You do not have permission to delete players.');
        } else {
          throw rpcError; // Rethrow other RPC errors
        }
      } else {
        // If successful, remove the player from the local state
        setPlayers((prev) => prev.filter((p) => p.id !== playerId));
        setError(null); // Clear any previous errors
      }
    } catch (err: any) {
      console.error('Error removing player:', err);
      setError(err.message || 'Unable to remove player.');
    }
  };

  async function fetchPlayers() {
    setLoadingPlayers(true);
    setError('');

    try {
      const { data, error: selectError } = await supabase
        .from('players')
        .select('*, age_group:age_groups(*)');

      if (selectError) {
        throw selectError;
      }

      const mapped = (data ?? []).map((row: any) => ({
        id: row.id,
        first_name: row.first_name,
        last_name: row.last_name,
        sex: row.sex,
        birthdate: row.birthdate,
        shirt_size: row.shirt_size,
        age_group_id: row.age_group_id,
        age_group: row.age_group,
        profile: row.profile
      }));

      setPlayers(mapped);
    } catch (err: any) {
      console.error(err);
      setError(err.message || 'Failed to fetch players.');
    } finally {
      setLoadingPlayers(false);
    }
  }

  useEffect(() => {
    fetchPlayers();
    fetchAgeGroups();
  }, []);

  // Fetch age groups from the database
  const fetchAgeGroups = async () => {
    setLoadingAgeGroups(true);
    setError('');

    try {
      const { data, error: fetchError } = await supabase
        .from('age_groups')
        .select('*')
        .order('sex')
        .order('group_name');
      
      if (profile?.role === 'coach') {
        const { data: assignedGroups, error: assignedGroupsError } = await supabase
          .from('coach_age_groups')
          .select('age_group_id')
          .eq('coach_id', profile?.id);

        if (assignedGroupsError) {
          throw assignedGroupsError;
        }

        // Filter age groups to only include those the coach is assigned to
        const filteredAgeGroups = data?.filter(ageGroup => assignedGroups?.some(group => group.age_group_id === ageGroup.id));

        setAgeGroups(filteredAgeGroups || []);
      }
      else {
        if (fetchError) {
          throw fetchError;
        }
        setAgeGroups(data || []);
      }

    } catch (err: any) {
      console.error('Error fetching age groups:', err);
      setError(err.message || 'Failed to fetch age groups');
    } finally {
      setLoadingAgeGroups(false);
    }
  };

  // Filter players based on search and filter criteria
  const filteredPlayers = players.filter((p) => {
    // Name search filter
    const checkStr = `${p.first_name ?? ''} ${p.last_name ?? ''}`.toLowerCase();
    const matchesSearch = checkStr.includes(search.toLowerCase());

    // Gender filter
    const matchesGender = genderFilter === 'all' || p.sex === genderFilter;

    // Age group filter
    const matchesAgeGroup = ageGroupFilter === 'all' || p.age_group_id === ageGroupFilter;

    // Coach access filter
    const matchesCoachAccess = (profile?.role === 'coach' && ageGroups?.some(ageGroup => ageGroup.id === p.age_group_id)) || profile?.role === 'administrator';

    // Positions filter
    const matchesPositions = positionsFilter.length === 0 ||
      (p.profile?.positions && positionsFilter.some(pos => p.profile?.positions?.includes(pos)));

    // Competitiveness filter
    const matchesCompetitiveness = competitivenessFilter.length === 0 ||
      (p.profile?.competitiveness && competitivenessFilter.some(comp => p.profile?.competitiveness?.includes(comp)));

    return matchesSearch && matchesCoachAccess && matchesGender && matchesAgeGroup && matchesPositions && matchesCompetitiveness;
  });

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      // Toggle direction if same field
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      // Set new field and default to ascending
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const getSortedPlayers = () => {
    return [...filteredPlayers].sort((a, b) => {
      // Apply appropriate sort logic based on the field
      let comparison = 0;

      switch (sortField) {
        case 'name':
          // Sort by full name
          const aName = `${a.first_name || ''} ${a.last_name || ''}`.trim();
          const bName = `${b.first_name || ''} ${b.last_name || ''}`.trim();
          comparison = aName.localeCompare(bName);
          break;

        case 'birthdate':
          // Sort by birthdate
          const aDate = a.birthdate || '';
          const bDate = b.birthdate || '';
          comparison = aDate.localeCompare(bDate);
          break;
      }

      // Apply sort direction
      return sortDirection === 'asc' ? comparison : -comparison;
    });
  };

  // Function to clear all filters
  const clearFilters = () => {
    setGenderFilter('all');
    setAgeGroupFilter('all');
    setPositionsFilter([]);
    setCompetitivenessFilter([]);
  };

  // Function to handle position filter checkbox changes
  const handlePositionFilterChange = (isChecked: boolean | 'indeterminate', position: string) => {
    if (typeof isChecked === 'boolean') {
      if (isChecked) {
        setPositionsFilter(prev => [...prev, position]);
      } else {
        setPositionsFilter(prev => prev.filter(p => p !== position));
      }
    }
  };

  // Function to handle competitiveness filter checkbox changes
  const handleCompetitivenessFilterChange = (isChecked: boolean | 'indeterminate', level: string) => {
    if (typeof isChecked === 'boolean') {
      if (isChecked) {
        setCompetitivenessFilter(prev => [...prev, level]);
      } else {
        setCompetitivenessFilter(prev => prev.filter(c => c !== level));
      }
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar />
      <main className="mx-auto max-w-7xl py-10 px-4 sm:px-6 lg:px-8">
        <div className="flex flex-col items-start justify-between gap-4 sm:flex-row">
          <div className="flex items-center space-x-2">
            <Users className="h-6 w-6 text-indigo-600" />
            <h1 className="text-2xl font-bold tracking-tight text-gray-800">
              Manage Players
            </h1>
          </div>
          {profile?.role === "administrator" && (
            <Button
              onClick={() => navigate('/player/new/edit')}
              className="flex items-center gap-2"
            >
              <Plus className="h-4 w-4" />
              Add Player
            </Button>
          )}
        </div>

        {error && (
          <div className="mt-6 rounded-md bg-red-50 p-4">
            <p className="text-sm text-red-600">{error}</p>
          </div>
        )}

        <section className="mt-8">
          <div className="mb-4 flex flex-col sm:flex-row gap-3">
            <div className="relative flex-grow">
              <Search className="absolute left-2 top-1/2 h-5 w-5 -translate-y-1/2 text-gray-400" />
              <Input
                type="text"
                placeholder="Search by name..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-9"
              />
            </div>
            <Button
              variant={showFilters ? "secondary" : "outline"}
              onClick={() => setShowFilters(!showFilters)}
              className="flex items-center gap-1"
            >
              <Filter className="h-4 w-4" />
              {showFilters ? "Hide Filters" : "Show Filters"}
            </Button>
          </div>

          {showFilters && (
            <div className="mb-6 p-4 border rounded-lg bg-white shadow-sm">
              <div className="flex justify-between items-center mb-3">
                <h3 className="text-lg font-medium">Filters</h3>
                <Button variant="ghost" size="sm" onClick={clearFilters} className="text-xs">
                  <X className="h-3.5 w-3.5 mr-1" /> Clear All
                </Button>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                {/* Gender Filter */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Gender</label>
                  <Select
                    value={genderFilter}
                    onValueChange={setGenderFilter}
                  >
                    <SelectTrigger className="w-full">
                      <SelectValue placeholder="All Genders" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Genders</SelectItem>
                      <SelectItem value="male">Male</SelectItem>
                      <SelectItem value="female">Female</SelectItem>
                      <SelectItem value="other">Other</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {/* Age Group Filter - Grouped by gender */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Age Group</label>
                  {loadingAgeGroups ? (
                    <div className="h-[38px] flex items-center justify-center bg-gray-50 border rounded-md">
                      <div className="h-4 w-4 animate-spin rounded-full border-b-2 border-indigo-600"></div>
                    </div>
                  ) : (
                    <Select
                      value={ageGroupFilter}
                      onValueChange={setAgeGroupFilter}
                    >
                      <SelectTrigger className="w-full">
                        <SelectValue placeholder="All Age Groups" />
                      </SelectTrigger>
                      <SelectContent>
                      <SelectItem value="all">All Age Groups</SelectItem>

                      {/* Show gender-specific age groups if a gender is selected */}
                      {genderFilter === 'male' && (
                        <>
                          <SelectItem value="male-header" disabled className="font-bold">
                            Boys
                          </SelectItem>
                          {ageGroups
                            .filter(group => group.sex === 'male')
                            .map(group => (
                              <SelectItem key={group.id} value={group.id}>
                                Boys {group.group_name}
                              </SelectItem>
                            ))
                          }
                        </>
                      )}

                      {genderFilter === 'female' && (
                        <>
                          <SelectItem value="female-header" disabled className="font-bold">
                            Girls
                          </SelectItem>
                          {ageGroups
                            .filter(group => group.sex === 'female')
                            .map(group => (
                              <SelectItem key={group.id} value={group.id}>
                                Girls {group.group_name}
                              </SelectItem>
                            ))
                          }
                        </>
                      )}

                      {/* Show all age groups if no gender is selected */}
                      {genderFilter === 'all' && (
                        <>
                          {ageGroups.some(group => group.sex === 'male') && (
                            <SelectItem value="male-header" disabled className="font-bold">
                              Boys
                            </SelectItem>
                          )}
                          {ageGroups
                            .filter(group => group.sex === 'male')
                            .map(group => (
                              <SelectItem key={group.id} value={group.id}>
                                Boys {group.group_name}
                              </SelectItem>
                            ))
                          }

                          {ageGroups.some(group => group.sex === 'female') && (
                            <SelectItem value="female-header" disabled className="font-bold mt-1">
                              Girls
                            </SelectItem>
                          )}
                          {ageGroups
                            .filter(group => group.sex === 'female')
                            .map(group => (
                              <SelectItem key={group.id} value={group.id}>
                                Girls {group.group_name}
                              </SelectItem>
                            ))
                          }
                        </>
                      )}
                      </SelectContent>
                    </Select>
                  )}
                </div>

                {/* Positions Filter */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Positions</label>
                  <div className="bg-white border rounded-md p-2 h-[38px] overflow-y-auto">
                    {positionsFilter.length > 0 ? (
                      <div className="flex flex-wrap gap-1">
                        {positionsFilter.map(pos => (
                          <Badge key={pos} variant="secondary" className="text-xs">
                            {pos}
                            <X
                              className="h-3 w-3 ml-1 cursor-pointer"
                              onClick={() => handlePositionFilterChange(false, pos)}
                            />
                          </Badge>
                        ))}
                      </div>
                    ) : (
                      <span className="text-gray-500 text-sm">All Positions</span>
                    )}
                  </div>
                  <div className="mt-2 grid grid-cols-2 gap-2">
                    {positionsOptions.map(position => (
                      <div key={position} className="flex items-center space-x-2">
                        <Checkbox
                          id={`pos-${position}`}
                          checked={positionsFilter.includes(position)}
                          onCheckedChange={(checked) => handlePositionFilterChange(checked, position)}
                        />
                        <label htmlFor={`pos-${position}`} className="text-sm cursor-pointer">
                          {position}
                        </label>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Competitiveness Filter */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Competitiveness</label>
                  <div className="bg-white border rounded-md p-2 h-[38px] overflow-y-auto">
                    {competitivenessFilter.length > 0 ? (
                      <div className="flex flex-wrap gap-1">
                        {competitivenessFilter.map(level => (
                          <Badge key={level} variant="secondary" className="text-xs">
                            {level}
                            <X
                              className="h-3 w-3 ml-1 cursor-pointer"
                              onClick={() => handleCompetitivenessFilterChange(false, level)}
                            />
                          </Badge>
                        ))}
                      </div>
                    ) : (
                      <span className="text-gray-500 text-sm">All Levels</span>
                    )}
                  </div>
                  <div className="mt-2 grid grid-cols-1 gap-2">
                    {competitivenessOptions.map(level => (
                      <div key={level} className="flex items-center space-x-2">
                        <Checkbox
                          id={`comp-${level}`}
                          checked={competitivenessFilter.includes(level)}
                          onCheckedChange={(checked) => handleCompetitivenessFilterChange(checked, level)}
                        />
                        <label htmlFor={`comp-${level}`} className="text-sm cursor-pointer">
                          {level}
                        </label>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Player count indicator */}
          <div className="mb-4 text-sm text-gray-600 flex items-center justify-between">
            <div>
              {filteredPlayers.length === players.length ? (
                <span className="font-medium">{players.length} Total Player{players.length !== 1 ? 's' : ''}</span>
              ) : (
                <span>
                  <span className="font-medium">{filteredPlayers.length}</span> of <span className="font-medium">{players.length}</span> Player{players.length !== 1 ? 's' : ''} (Filtered)
                </span>
              )}
            </div>
            {filteredPlayers.length !== players.length && (
              <Button
                variant="ghost"
                size="sm"
                onClick={clearFilters}
                className="text-xs hover:text-indigo-600 transition-colors"
              >
                Clear Filters
              </Button>
            )}
          </div>

          {loadingPlayers ? (
            <div className="flex items-center justify-center py-10">
              <div className="h-12 w-12 animate-spin rounded-full border-b-2 border-indigo-600"></div>
            </div>
          ) : filteredPlayers.length === 0 ? (
            <p className="text-gray-600">No players found.</p>
          ) : (
            <div className="overflow-hidden rounded-lg border bg-white shadow-sm">
              <Table>
                <TableHeader>
                  <TableRow className="bg-gray-50">
                    <TableHead
                      className="cursor-pointer hover:bg-gray-100"
                      onClick={() => handleSort('name')}
                    >
                      <div className="flex items-center">
                        Name
                        {sortField === 'name' && (
                          <span className="ml-1">
                            {sortDirection === 'asc' ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                          </span>
                        )}
                      </div>
                    </TableHead>
                    <TableHead>Gender</TableHead>
                    <TableHead
                      className="cursor-pointer hover:bg-gray-100"
                      onClick={() => handleSort('birthdate')}
                    >
                      <div className="flex items-center">
                        Birthdate
                        {sortField === 'birthdate' && (
                          <span className="ml-1">
                            {sortDirection === 'asc' ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                          </span>
                        )}
                      </div>
                    </TableHead>
                    <TableHead>Positions</TableHead>
                    <TableHead>Competitiveness</TableHead>
                    {profile?.role === "administrator" && <TableHead>Actions</TableHead>}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {getSortedPlayers().map((player) => {
                    const displayName =
                      player.first_name || player.last_name
                        ? `${player.first_name ?? ''} ${player.last_name ?? ''}`.trim()
                        : '—';
                    const positionsStr = player.profile?.positions?.join(', ') || '—';
                    const competitivenessStr = player.profile?.competitiveness?.join(', ') || '—';

                    const birthdateStr = player.birthdate
                      ? formatDateString(player.birthdate, 'en-US', 'short')
                      : '—';

                    return (
                      <TableRow
                        key={player.id}
                        className="hover:bg-gray-50 cursor-pointer"
                      >
                        <TableCell className="whitespace-nowrap">{displayName}</TableCell>
                        <TableCell className="whitespace-nowrap">
                          {player.sex ?
                            player.sex.charAt(0).toUpperCase() + player.sex.slice(1).toLowerCase()
                            : '—'}
                        </TableCell>
                        <TableCell className="whitespace-nowrap">{birthdateStr}</TableCell>
                        <TableCell className="whitespace-nowrap">{positionsStr}</TableCell>
                        <TableCell className="whitespace-nowrap">{competitivenessStr}</TableCell>
                        {profile?.role === "administrator" && (
                          <TableCell className="whitespace-nowrap">
                            <div className="flex items-center space-x-2" onClick={(e) => e.stopPropagation()}>
                              <Button
                                variant="ghost"
                                size="icon"
                                onClick={() => navigate(`/player/${player.id}/edit`)}
                                title="Edit Player"
                              >
                                <Edit3 className="h-4 w-4" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="icon"
                                onClick={() => handleRemovePlayer(player.id)}
                                title="Remove Player"
                                className="text-red-600 hover:bg-red-50"
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                              <Button
                                variant="outline"
                                size="sm"
                                className="h-8 text-blue-600 border-blue-600 hover:bg-blue-50"
                                onClick={() => navigate(`/today?lastname=${encodeURIComponent(player.last_name || '')}&birthdate=${encodeURIComponent(player.birthdate || '')}`)}
                              >
                                <Users className="w-3 h-3 mr-1" />
                                Check In
                              </Button>
                            </div>
                          </TableCell>
                        )}
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          )}
        </section>

      </main>
    </div>
  );
}